/**
 * CustomerProfileScreen.jsx
 * Customer profile with personal data, payment methods, and editing capability
 */
import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, MapPin, Calendar, CreditCard, Plus, Trash2, 
  ChevronLeft, ChevronRight, Shield, Check, Edit2, Save, X, RefreshCw,
  AlertCircle, Heart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const CustomerProfileScreen = ({ 
  customer,
  paymentMethods = [],
  onBack,
  onAddPaymentMethod,
  onDeletePaymentMethod,
  onSetDefaultPaymentMethod,
  portalToken,
  onProfileUpdate
}) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Editable form data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    street: '',
    zip: '',
    city: '',
    birthdate: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    height_cm: ''
  });

  // Initialize form data when customer changes
  useEffect(() => {
    if (customer) {
      setFormData({
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        phone: customer.phone || '',
        street: customer.street || customer.address || '',
        zip: customer.zip || '',
        city: customer.city || '',
        birthdate: customer.birthdate ? customer.birthdate.split('T')[0] : '',
        emergency_contact_name: customer.emergency_contact_name || '',
        emergency_contact_phone: customer.emergency_contact_phone || '',
        height_cm: customer.height_cm || customer.height || ''
      });
    }
  }, [customer]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
  };

  // Get card brand icon color
  const getCardBrandColor = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa': return 'text-blue-400';
      case 'mastercard': return 'text-orange-400';
      case 'amex': return 'text-blue-500';
      default: return 'text-zinc-400';
    }
  };

  const handleSaveProfile = async () => {
    if (!portalToken) {
      toast.error('Nicht angemeldet');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/portal/auth/profile`,
        formData,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      toast.success('Profil gespeichert');
      setIsEditing(false);
      // Notify parent to refresh data
      onProfileUpdate?.();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset form to original customer data
    if (customer) {
      setFormData({
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        phone: customer.phone || '',
        street: customer.street || customer.address || '',
        zip: customer.zip || '',
        city: customer.city || '',
        birthdate: customer.birthdate ? customer.birthdate.split('T')[0] : '',
        emergency_contact_name: customer.emergency_contact_name || '',
        emergency_contact_phone: customer.emergency_contact_phone || '',
        height_cm: customer.height_cm || customer.height || ''
      });
    }
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Gold color
  const goldColor = '#C9A227';

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-800"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-white text-base font-bold">Kundenprofil</h1>
        </div>
        
        {/* Edit/Save Button */}
        {activeTab === 'profile' && (
          isEditing ? (
            <div className="flex gap-2">
              <button 
                onClick={handleCancelEdit}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-800"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
              <button 
                onClick={handleSaveProfile}
                disabled={saving}
                className="w-8 h-8 flex items-center justify-center rounded-lg"
                style={{ backgroundColor: goldColor }}
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 text-black animate-spin" />
                ) : (
                  <Save className="w-4 h-4 text-black" />
                )}
              </button>
            </div>
          ) : (
            <button 
              onClick={() => setIsEditing(true)}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-800"
              data-testid="edit-profile-btn"
            >
              <Edit2 className="w-4 h-4 text-yellow-400" />
            </button>
          )
        )}
      </div>

      {/* Profile Avatar */}
      <div className="flex flex-col items-center py-4">
        <div className="w-20 h-20 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center text-black text-2xl font-bold mb-2">
          {(isEditing ? formData.first_name : customer?.first_name)?.charAt(0)?.toUpperCase() || 'K'}
        </div>
        {isEditing ? (
          <div className="flex gap-2 w-full max-w-xs">
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              placeholder="Vorname"
              className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm text-center"
            />
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              placeholder="Nachname"
              className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm text-center"
            />
          </div>
        ) : (
          <>
            <h2 className="text-white font-bold text-lg">
              {customer?.first_name} {customer?.last_name}
            </h2>
            <p className="text-zinc-400 text-xs">Premium Mitglied</p>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-zinc-800/50 p-1 rounded-xl">
        <button 
          onClick={() => { setActiveTab('profile'); setIsEditing(false); }}
          className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
            activeTab === 'profile' 
              ? 'bg-yellow-500 text-black' 
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          Profil
        </button>
        <button 
          onClick={() => { setActiveTab('payment'); setIsEditing(false); }}
          className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
            activeTab === 'payment' 
              ? 'bg-yellow-500 text-black' 
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          Zahlungsmethoden
        </button>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="space-y-3">
          {/* Contact Information */}
          <div className="bg-zinc-800/50 rounded-xl overflow-hidden">
            <div className="p-3 border-b border-zinc-700/50">
              <p className="text-zinc-400 text-xs font-medium uppercase tracking-wider">Kontaktdaten</p>
            </div>
            
            {/* Email - Not editable */}
            <div className="p-3 flex items-center gap-3 border-b border-zinc-700/50">
              <Mail className="w-4 h-4 text-yellow-400" />
              <div className="flex-1">
                <p className="text-zinc-400 text-[10px]">E-Mail</p>
                <p className="text-white text-sm">{customer?.email || '-'}</p>
              </div>
              {isEditing && (
                <span className="text-zinc-600 text-[10px]">Nicht änderbar</span>
              )}
            </div>
            
            {/* Phone - Editable */}
            <div className="p-3 flex items-center gap-3 border-b border-zinc-700/50">
              <Phone className="w-4 h-4 text-yellow-400" />
              <div className="flex-1">
                <p className="text-zinc-400 text-[10px]">Telefon</p>
                {isEditing ? (
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+41 79 123 45 67"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-white text-sm mt-1"
                  />
                ) : (
                  <p className="text-white text-sm">{customer?.phone || '-'}</p>
                )}
              </div>
            </div>
            
            {/* Birthday - Editable */}
            <div className="p-3 flex items-center gap-3">
              <Calendar className="w-4 h-4 text-yellow-400" />
              <div className="flex-1">
                <p className="text-zinc-400 text-[10px]">Geburtstag</p>
                {isEditing ? (
                  <input
                    type="date"
                    value={formData.birthdate}
                    onChange={(e) => handleInputChange('birthdate', e.target.value)}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-white text-sm mt-1"
                  />
                ) : (
                  <p className="text-white text-sm">{formatDate(customer?.birthdate)}</p>
                )}
              </div>
            </div>
          </div>

          {/* Address - Editable */}
          <div className="bg-zinc-800/50 rounded-xl overflow-hidden">
            <div className="p-3 border-b border-zinc-700/50">
              <p className="text-zinc-400 text-xs font-medium uppercase tracking-wider">Adresse</p>
            </div>
            
            <div className="p-3">
              {isEditing ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={formData.street}
                    onChange={(e) => handleInputChange('street', e.target.value)}
                    placeholder="Strasse und Hausnummer"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-white text-sm"
                  />
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.zip}
                      onChange={(e) => handleInputChange('zip', e.target.value)}
                      placeholder="PLZ"
                      className="w-24 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-white text-sm"
                    />
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="Stadt"
                      className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-white text-sm"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-yellow-400 mt-0.5" />
                  <div className="flex-1">
                    {customer?.street || customer?.address ? (
                      <>
                        <p className="text-white text-sm">{customer?.street || customer?.address}</p>
                        <p className="text-white text-sm">{customer?.zip} {customer?.city}</p>
                        {customer?.country && (
                          <p className="text-zinc-400 text-sm">{customer?.country}</p>
                        )}
                      </>
                    ) : (
                      <p className="text-zinc-500 text-sm">Keine Adresse hinterlegt</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Member Since - Not editable */}
          <div className="bg-zinc-800/50 rounded-xl p-3 flex items-center gap-3">
            <Shield className="w-4 h-4 text-yellow-400" />
            <div className="flex-1">
              <p className="text-zinc-400 text-[10px]">Mitglied seit</p>
              <p className="text-white text-sm">{formatDate(customer?.created_at)}</p>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="bg-zinc-800/50 rounded-xl overflow-hidden" data-testid="emergency-contact-section">
            <div className="p-3 border-b border-zinc-700/50 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <p className="text-zinc-400 text-xs font-medium uppercase tracking-wider">Notfallkontakt</p>
            </div>
            
            {isEditing ? (
              <div className="p-3 space-y-2">
                <div>
                  <p className="text-zinc-400 text-[10px] mb-1">Name</p>
                  <input
                    type="text"
                    value={formData.emergency_contact_name}
                    onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
                    placeholder="Max Mustermann"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <p className="text-zinc-400 text-[10px] mb-1">Telefon</p>
                  <input
                    type="tel"
                    value={formData.emergency_contact_phone}
                    onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
                    placeholder="+41 79 123 45 67"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-white text-sm"
                  />
                </div>
                <p className="text-zinc-500 text-[10px]">Diese Person wird im Notfall kontaktiert</p>
              </div>
            ) : (
              <div className="p-3">
                {customer?.emergency_contact_name || customer?.emergency_contact_phone ? (
                  <div className="flex items-start gap-3">
                    <Heart className="w-4 h-4 text-red-400 mt-0.5" />
                    <div>
                      <p className="text-white text-sm font-medium">
                        {customer?.emergency_contact_name || 'Unbekannt'}
                      </p>
                      <p className="text-zinc-400 text-sm">
                        {customer?.emergency_contact_phone || 'Keine Nummer'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-zinc-500">
                    <Heart className="w-4 h-4" />
                    <p className="text-sm">Kein Notfallkontakt hinterlegt</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Edit Mode Info */}
          {isEditing && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 flex items-start gap-2">
              <Edit2 className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
              <p className="text-zinc-300 text-xs">
                Bearbeiten Sie Ihre Daten und tippen Sie auf das Speichern-Symbol oben rechts.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Payment Methods Tab */}
      {activeTab === 'payment' && (
        <div className="space-y-3">
          {/* Add Payment Method Button */}
          <button 
            onClick={onAddPaymentMethod}
            className="w-full flex items-center justify-center gap-2 py-3 bg-yellow-500 text-black rounded-xl font-medium text-sm"
          >
            <Plus className="w-4 h-4" />
            Zahlungsmethode hinzufügen
          </button>

          {/* Payment Methods List */}
          {paymentMethods.length === 0 ? (
            <div className="bg-zinc-800/50 rounded-xl p-6 text-center">
              <CreditCard className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">Keine Zahlungsmethoden hinterlegt</p>
              <p className="text-zinc-500 text-xs mt-1">
                Fügen Sie eine Karte hinzu für schnellere Zahlungen
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {paymentMethods.map((method, index) => (
                <div 
                  key={method.id || index}
                  className="bg-zinc-800/50 rounded-xl p-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-8 bg-zinc-700 rounded-lg flex items-center justify-center">
                      <CreditCard className={`w-5 h-5 ${getCardBrandColor(method.brand)}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-medium">
                          {method.brand || 'Karte'} •••• {method.last4 || '****'}
                        </p>
                        {method.is_default && (
                          <span className="px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 text-[10px] rounded">
                            Standard
                          </span>
                        )}
                      </div>
                      <p className="text-zinc-500 text-xs">
                        Läuft ab: {method.exp_month}/{method.exp_year}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-500" />
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-700/50">
                    {!method.is_default && (
                      <button 
                        onClick={() => onSetDefaultPaymentMethod?.(method.id)}
                        className="flex-1 py-2 bg-zinc-700 rounded-lg text-zinc-300 text-xs flex items-center justify-center gap-1"
                      >
                        <Check className="w-3 h-3" />
                        Als Standard
                      </button>
                    )}
                    <button 
                      onClick={() => onDeletePaymentMethod?.(method.id)}
                      className="flex-1 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs flex items-center justify-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Entfernen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Security Note */}
          <div className="bg-zinc-800/30 rounded-xl p-3 flex items-start gap-2">
            <Shield className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
            <p className="text-zinc-500 text-xs">
              Ihre Zahlungsdaten werden sicher und verschlüsselt gespeichert. 
              Wir haben keinen Zugriff auf Ihre vollständigen Kartendaten.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerProfileScreen;
